/*
  This file contains definition of contract for SDK event handlers
  All modifications should be reflected in client apps (miniapps, external apps, etc.)
*/

import { HQO_SDK_EVENT_SUFFIX } from '../sdk.constants';
import { HQO_SDK_ACTIONS } from './sdk-message.types';

export type SdkResponseEvent<T extends string> = `${T}${typeof HQO_SDK_EVENT_SUFFIX}`;

type SdkResponseActionPayloads = {
  [HQO_SDK_ACTIONS.INIT]: InitHandlerPayload;
  [HQO_SDK_ACTIONS.GET_JWT]: GetJWTHandlerPayload;
  [HQO_SDK_ACTIONS.GET_USER]: GetUserHandlerPayload;
  [HQO_SDK_ACTIONS.GET_BUILDING]: GetBuildingHandlerPayload;
  [HQO_SDK_ACTIONS.GET_BUILDINGS]: GetBuildingsHandlerPayload;
  [HQO_SDK_ACTIONS.GET_HOST_INFO]: GetHostInfoHandlerPayload;
  [HQO_SDK_ACTIONS.SHOW_NATIVE_PAYMENT]: ShowNativePaymentHandlerPayload;
  [HQO_SDK_ACTIONS.HIDE_NATIVE_PAYMENTS]: HideNativePaymentHandlerPayload;
  [HQO_SDK_ACTIONS.GET_PAYMENT_METHODS]: GetPaymentMethodsHandlerPayload;
  [HQO_SDK_ACTIONS.TOKENIZE_CARD]: TokenizeCardHandlerPayload;
  [HQO_SDK_ACTIONS.GET_BRAND]: GetBrandHandlerPayload;
  [HQO_SDK_ACTIONS.GET_THEME]: GetThemeHandlerPayload;
  [HQO_SDK_ACTIONS.GET_STORAGE]: GetStorageHandlerPayload;
  [HQO_SDK_ACTIONS.SET_STORAGE]: SetStorageHandlerPayload;
  [HQO_SDK_ACTIONS.SET_HEADER]: SetHeaderHandlerPayload;
  [HQO_SDK_ACTIONS.SHOW_HEADER]: HeaderVisibilityHandlerPayload;
  [HQO_SDK_ACTIONS.HIDE_HEADER]: HeaderVisibilityHandlerPayload;
  [HQO_SDK_ACTIONS.SHOW_FOOTER]: FooterVisibilityHandlerPayload;
  [HQO_SDK_ACTIONS.HIDE_FOOTER]: FooterVisibilityHandlerPayload;
  [HQO_SDK_ACTIONS.CLOSE_WEB_VIEW]: CloseWebViewHandlerPayload;
};

export type SdkEvents = SdkResponseEvent<keyof SdkResponseActionPayloads>;

export type SdkHandlerPayload<T extends SdkEvents> = {
  [K in keyof SdkResponseActionPayloads as SdkResponseEvent<K>]: SdkResponseActionPayloads[K];
}[T];

export interface InitHandlerPayload {
  user: User;
  building: Building;
  base_url: string;
  session_token: string;
  jwt_token: string;
  theme: Theme;
  brand: Brand;
  initial_route: string;
}

export interface GetJWTHandlerPayload {
  token: string;
}

export type GetUserHandlerPayload = User;

export type GetBuildingHandlerPayload = Building;

export interface GetBuildingsHandlerPayload {
  data: Building[];
}

export interface GetHostInfoHandlerPayload {
  host: HostInfo;
}

export interface ShowNativePaymentHandlerPayload {
  success: boolean;
  status: string;
  id: number;
}

export type HideNativePaymentHandlerPayload = void;

export interface GetPaymentMethodsHandlerPayload {
  apple_pay: boolean;
  google_pay: boolean;
  cards: PaymentMethodCard[];
}

export interface TokenizeCardHandlerPayload {
  card_id: number;
  token: string;
}

export type GetStorageHandlerPayload<T extends string = string> = Record<T, string>;

export type SetStorageHandlerPayload = void;

export type SetHeaderHandlerPayload = void;

export type GetThemeHandlerPayload = Theme;

export type GetBrandHandlerPayload = Brand;

export type CloseWebViewHandlerPayload = void;

export interface HeaderVisibilityHandlerPayload {
  visible: boolean;
}

export interface FooterVisibilityHandlerPayload {
  visible: boolean;
}

export interface User {
  uuid: string;
  first_name: string;
  last_name: string;
  email: string;
  company: UserCompany;
}

export interface UserCompany {
  uuid: string;
  name: string;
}

export interface Building {
  uuid: string;
  name: string;
  address: string;
  theme: BuildingTheme;
  timezone: string;
}

export interface BuildingTheme {
  theme: Theme;
}

export interface Theme {
  primary1: string;
  primary2: string;
  fontColor?: string;
  font?: {
    body: Font;
    heading: Font;
    title: Font;
  };
}

interface Font {
  url: string;
  font_family: string;
}

export interface Brand {
  brand: string;
}

export interface HostInfo {
  version: string;
  theme: BuildingTheme;
  os_version: string;
  os_name: string;
  device_name: string;
  device_family: string;
  application_version: string;
  application_name: string;
  company_name: string;
}

export interface PaymentMethodCard {
  card_last_four: string;
  card_brand: string;
  token_id: number;
  token: string;
}
